import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { ITicTacToeGame, IGameResult, IPenaltyItem } from "../ts/Definitions";
import { SelectedSettingsContext } from "../ts/Contexts";
import { set } from "lodash";

interface TicTacToeDialogProps {
  game: ITicTacToeGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

type Cell = "" | "X" | "O";

const emptyBoard = (): Cell[][] => [
  ["", "", ""],
  ["", "", ""],
  ["", "", ""],
];

function checkWinner(board: Cell[][]): Cell | null {
  // Rows, columns, diagonals
  for (let i = 0; i < 3; i++) {
    if (board[i][0] && board[i][0] === board[i][1] && board[i][1] === board[i][2]) return board[i][0];
    if (board[0][i] && board[0][i] === board[1][i] && board[1][i] === board[2][i]) return board[0][i];
  }
  if (board[0][0] && board[0][0] === board[1][1] && board[1][1] === board[2][2]) return board[0][0];
  if (board[0][2] && board[0][2] === board[1][1] && board[1][1] === board[2][0]) return board[0][2];
  return null;
}

function isBoardFull(board: Cell[][]): boolean {
  return board.every(row => row.every(cell => cell !== ""));
}

function getRandomMove(board: Cell[][]): [number, number] | null {
  const empty: [number, number][] = [];
  board.forEach((row, r) => row.forEach((cell, c) => { if (!cell) empty.push([r, c]); }));
  if (empty.length === 0) return null;
  return empty[Math.floor(Math.random() * empty.length)];
}

// Find a winning move for a given player
function findWinningMove(board: Cell[][], player: Cell): [number, number] | null {
  // Check rows
  for (let r = 0; r < 3; r++) {
    const row = board[r];
    const playerCount = row.filter(c => c === player).length;
    const emptyCount = row.filter(c => c === "").length;
    if (playerCount === 2 && emptyCount === 1) {
      const col = row.findIndex(c => c === "");
      return [r, col];
    }
  }

  // Check columns
  for (let c = 0; c < 3; c++) {
    const col = [board[0][c], board[1][c], board[2][c]];
    const playerCount = col.filter(cell => cell === player).length;
    const emptyCount = col.filter(cell => cell === "").length;
    if (playerCount === 2 && emptyCount === 1) {
      const row = col.findIndex(cell => cell === "");
      return [row, c];
    }
  }

  // Check main diagonal (top-left to bottom-right)
  const mainDiag = [board[0][0], board[1][1], board[2][2]];
  const mainDiagPlayerCount = mainDiag.filter(c => c === player).length;
  const mainDiagEmptyCount = mainDiag.filter(c => c === "").length;
  if (mainDiagPlayerCount === 2 && mainDiagEmptyCount === 1) {
    const idx = mainDiag.findIndex(c => c === "");
    return [idx, idx];
  }

  // Check anti-diagonal (top-right to bottom-left)
  const antiDiag = [board[0][2], board[1][1], board[2][0]];
  const antiDiagPlayerCount = antiDiag.filter(c => c === player).length;
  const antiDiagEmptyCount = antiDiag.filter(c => c === "").length;
  if (antiDiagPlayerCount === 2 && antiDiagEmptyCount === 1) {
    const positions: [number, number][] = [[0, 2], [1, 1], [2, 0]];
    const idx = antiDiag.findIndex(c => c === "");
    return positions[idx];
  }

  return null;
}

const TicTacToeDialog: React.FC<TicTacToeDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { rounds, minPassRate, key, userid, username } = game;
  const [currentRound, setCurrentRound] = useState(1);
  const [board, setBoard] = useState<Cell[][]>(emptyBoard());
  const [playerTurn, setPlayerTurn] = useState<"X" | "O">("X");
  const [playerWins, setPlayerWins] = useState(0);
  const [aiWins, setAiWins] = useState(0);
  const [draws, setDraws] = useState(0);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [penalties, setPenalties] = useState<(IPenaltyItem)[]>([]);

  useEffect(() => {
    const newPens = penaltyKeys?.map(key => settings.getPenalty(key)).filter(p => p !== undefined) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  // Handle player move
  const handleCellClick = (row: number, col: number) => {
    if (finished || board[row][col] || playerTurn !== "X") return;
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = "X";
    setBoard(newBoard);
    setPlayerTurn("O");
    setTimeout(() => aiMove(newBoard), 400);
  };

  // AI makes a move with 50% chance to block player's winning move
  const aiMove = (currentBoard: Cell[][]) => {
    if (finished) return;

    let move: [number, number] | null = null;

    // Check if player has a winning move
    const playerWinningMove = findWinningMove(currentBoard, "X");

    // Block 50% of the time if player has a winning move
    if (playerWinningMove && Math.random() < 0.5) {
      move = playerWinningMove;
    } else {
      // Otherwise make a random move
      move = getRandomMove(currentBoard);
    }

    if (!move) return;
    const [r, c] = move;
    const newBoard = currentBoard.map(row => [...row]);
    newBoard[r][c] = "O";
    setBoard(newBoard);
    setPlayerTurn("X");
  };

  // Add a ref to track the winner of the last round
  const lastRoundWinner = React.useRef<"X" | "O" | "draw" | null>(null);

  React.useEffect(() => {
    if (resetting) return;
    const winner = checkWinner(board);
    if (winner) {
      lastRoundWinner.current = winner;
      if (winner === "X") {
        setPlayerWins(w => w + 1);
        setMessage("You win this round!");
      } else {
        setAiWins(w => w + 1);
        setMessage("AI wins this round!");
      }
      setResetting(true);
      setTimeout(() => nextRound(), 1200);
    } else if (isBoardFull(board)) {
      lastRoundWinner.current = "draw";
      setDraws(d => d + 1);
      setMessage("Draw!");
      setResetting(true);
      setTimeout(() => nextRound(), 1200);
    }
    // eslint-disable-next-line
  }, [board]);

  const nextRound = () => {
    if (currentRound >= rounds) {
      // Calculate total player wins including the last round if player won
      const totalPlayerWins =
        playerWins + (lastRoundWinner.current === "X" ? 1 : 0);
      setFinished(true);
      setMessage(
        `Game Over! You won ${totalPlayerWins} of ${rounds} rounds. Pass rate required: ${minPassRate}`
      );
    } else {
      setCurrentRound(r => r + 1);
      setBoard(emptyBoard());
      setPlayerTurn("X");
      setMessage(null);
      setResetting(false);
      lastRoundWinner.current = null;
    }
  };

  const handleClose = () => {
    onClose({
      gameKey: key,
      penaltyCount: playerWins >= minPassRate ? 0 : minPassRate - playerWins,
      completed: playerWins,
      total: rounds,
      totalTime: rounds,
      mistakes: rounds - playerWins,
      userid,
      username,
      date: new Date(),
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>Tic Tac Toe</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Win at least {minPassRate} out of {rounds} rounds!
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Round: {currentRound} / {rounds}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {penalties.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Penalty for incorrect answers: <b>{penalties.map(p => p.name).join(", ")}</b>
            </Alert>
          )}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Your Wins: {playerWins}</Typography>
          <Typography variant="body2">AI Wins: {aiWins}</Typography>
          <Typography variant="body2">Draws: {draws}</Typography>
        </Box>
        <Grid container spacing={1} justifyContent="center">
          {board.map((rowArr, rowIdx) => (
            <Grid key={rowIdx} container item spacing={1} justifyContent="center">
              {rowArr.map((cell, colIdx) => (
                <Grid item key={colIdx}>
                  <Paper
                    elevation={2}
                    sx={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                      cursor: finished || cell ? "default" : "pointer",
                      fontWeight: "bold",
                      fontSize: 28,
                      userSelect: "none",
                    }}
                    onClick={() => handleCellClick(rowIdx, colIdx)}
                  >
                    {cell}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ))}
        </Grid>
        {message && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color={finished && playerWins >= minPassRate ? "success.main" : finished ? "error.main" : "text.primary"}>
              {message}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {finished && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TicTacToeDialog;